import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown } from "lucide-react";

/**
 * Combobox de repuestos con filtro de texto.
 * Permite escribir para filtrar el catálogo y seleccionar rápidamente.
 *
 * Props:
 *  - catalogo: [{ id, sku, nombre, categoria }]
 *  - value: id del repuesto seleccionado (o "")
 *  - onSelect: (id) => void
 *  - disabled: bool
 *  - placeholder: string
 */
export default function RepuestoCombobox({ catalogo = [], value, onSelect, disabled, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  const selected = catalogo.find((r) => r.id === value);
  const norm = (s) => (s || "").toLowerCase();
  const q = norm(query);
  const filtered = q
    ? catalogo.filter((r) => norm(`${r.nombre} ${r.sku} ${r.categoria}`).includes(q))
    : catalogo;

  const display = open ? query : (selected?.nombre || "");

  return (
    <div className="relative flex-1">
      <div className="relative">
        <Input
          value={display}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
          placeholder={placeholder || "Buscar repuesto..."}
          disabled={disabled}
          className="bg-white h-9 text-sm pr-8"
        />
        <ChevronDown className="h-4 w-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
      {open && !disabled && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
          onMouseDown={() => { if (blurTimer.current) clearTimeout(blurTimer.current); }}
        >
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-400">Sin resultados para "{query}"</p>
          )}
          {filtered.map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={() => { onSelect(r.id); setOpen(false); setQuery(""); }}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-[#0FB4B7]/10 ${
                r.id === value ? "bg-[#0FB4B7]/10 font-medium" : ""
              }`}
            >
              {r.nombre}
              <span className="text-xs text-gray-400 ml-1">({r.categoria})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
